import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  Alert,
  FormHelperText,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import FormSectionHeader from '../common/FormSectionHeader';
import { DESIGNATION_CHOICES, DEPARTMENT_CHOICES, CURRENCY_CHOICES } from '../../utils/employeeConstants';

const EmployeeForm = ({ open, mode, formData, formErrors, onClose, onSubmit, onInputChange, shifts = [] }) => {
  // Helper function to check if resignation date has a valid value
  const hasResignationDate = (resignationDate) =>
    resignationDate && resignationDate !== null && resignationDate.trim() !== '';

  // Auto-calculate take home salary
  useEffect(() => {
    const basicSalary = parseFloat(formData.basic_salary) || 0;
    const transportAllowance = parseFloat(formData.transport_allowance) || 0;
    const otherAllowances = parseFloat(formData.other_allowances) || 0;
    const taxDeductions = parseFloat(formData.tax_deductions) || 0;

    const calculatedTakeHome = basicSalary + transportAllowance + otherAllowances - taxDeductions;
    const currentTakeHome = parseFloat(formData.take_home_salary) || 0;

    // Only update if the calculated value is different from current value
    if (calculatedTakeHome !== currentTakeHome) {
      onInputChange('take_home_salary', calculatedTakeHome.toString());
    }
  }, [
    formData.basic_salary,
    formData.transport_allowance,
    formData.other_allowances,
    formData.tax_deductions,
    formData.take_home_salary,
    onInputChange,
  ]);

  useEffect(() => {
    if (hasResignationDate(formData.resignation_date)) {
      // If resignation date is provided, set is_active to false
      if (formData.is_active !== false) {
        onInputChange('is_active', false);
      }
    }
  }, [formData.resignation_date, formData.is_active, onInputChange]);

  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New Employee';
      case 'edit':
        return 'Edit Employee';
      case 'view':
        return 'Employee Details';
      default:
        return 'Employee';
    }
  };

  const isViewMode = mode === 'view';

  const getDialogSubtitle = () => {
    if (isViewMode) return 'Read-only summary of this employee record.';
    if (mode === 'add')
      return 'Fill in required fields marked on each input. Salary and active status update automatically where noted.';
    return 'Update details below. Changing resignation date will mark the employee inactive.';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
          {getDialogTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400, lineHeight: 1.65 }}>
          {getDialogSubtitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 0.5 }}>
          {!isViewMode && (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              Bank and email fields are optional. Take-home pay is calculated from salary components below.
            </Alert>
          )}

          <FormSectionHeader
            title="Basic information"
            description="Core identity and contact details used across HR, attendance, and payroll."
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID"
                value={formData.employee_id}
                onChange={(e) => onInputChange('employee_id', e.target.value)}
                error={!!formErrors.employee_id}
                helperText={formErrors.employee_id || 'Unique ID shown on reports and payroll (e.g. EMP001).'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => onInputChange('full_name', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.full_name}
                helperText={formErrors.full_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => onInputChange('email', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.email}
                helperText={
                  formErrors.email || 'Optional — used for notifications and portal access if an account is created.'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => onInputChange('phone', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.phone}
                helperText={formErrors.phone || 'Primary contact number for HR outreach.'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Identity Number"
                value={formData.identity_number}
                onChange={(e) => onInputChange('identity_number', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.identity_number}
                helperText={formErrors.identity_number || 'Government or national ID, if applicable.'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.shift}>
                <InputLabel>Shift</InputLabel>
                <Select
                  value={formData.shift ?? ''}
                  label="Shift"
                  onChange={(e) => onInputChange('shift', e.target.value)}
                  disabled={isViewMode}
                >
                  <MenuItem value="">None</MenuItem>
                  {shifts.map((shift) => (
                    <MenuItem key={shift.id} value={shift.id}>
                      {`${shift.name} (${(shift.shift_start_time || '').slice(0, 5)} - ${(
                        shift.shift_end_time || ''
                      ).slice(0, 5)} | Grace: ${shift.grace_time_minutes ?? 0} min)`}
                    </MenuItem>
                  ))}
                </Select>
                {(formErrors.shift || !isViewMode) && (
                  <FormHelperText error={!!formErrors.shift}>
                    {formErrors.shift || 'Defines expected hours and grace period for attendance.'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <FormSectionHeader
            title="Job information"
            description="Role and department determine filters, reporting, and organizational structure."
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.designation}>
                <InputLabel>Designation</InputLabel>
                <Select
                  value={formData.designation}
                  label="Designation"
                  onChange={(e) => onInputChange('designation', e.target.value)}
                  disabled={isViewMode}
                >
                  {DESIGNATION_CHOICES.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {(formErrors.designation || !isViewMode) && (
                  <FormHelperText error={!!formErrors.designation}>
                    {formErrors.designation || 'Job title as it should appear on records.'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.department}>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department}
                  label="Department"
                  onChange={(e) => onInputChange('department', e.target.value)}
                  disabled={isViewMode}
                >
                  {DEPARTMENT_CHOICES.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {(formErrors.department || !isViewMode) && (
                  <FormHelperText error={!!formErrors.department}>
                    {formErrors.department || 'Team or function this employee belongs to.'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <FormSectionHeader
            title="Salary information"
            description="Enter pay components in the selected currency. Take-home updates automatically when amounts change."
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Salary Currency</InputLabel>
                <Select
                  value={formData.salary_currency}
                  label="Salary Currency"
                  onChange={(e) => onInputChange('salary_currency', e.target.value)}
                  disabled={isViewMode}
                >
                  {CURRENCY_CHOICES.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {!isViewMode && <FormHelperText>Applies to all salary fields on this form.</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Basic Salary"
                type="number"
                value={formData.basic_salary}
                onChange={(e) =>
                  onInputChange(
                    'basic_salary',
                    e.target.value === '' ? '' : String(Math.max(0, parseFloat(e.target.value) || 0))
                  )
                }
                disabled={isViewMode}
                error={!!formErrors.basic_salary}
                helperText={formErrors.basic_salary || 'Base monthly pay before allowances and deductions.'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.salary_currency}</InputAdornment>,
                  inputProps: { min: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Transport Allowance"
                type="number"
                value={formData.transport_allowance}
                onChange={(e) =>
                  onInputChange(
                    'transport_allowance',
                    e.target.value === '' ? '' : String(Math.max(0, parseFloat(e.target.value) || 0))
                  )
                }
                disabled={isViewMode}
                helperText="Commuting or travel allowance, if any."
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.salary_currency}</InputAdornment>,
                  inputProps: { min: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Other Allowances"
                type="number"
                value={formData.other_allowances}
                onChange={(e) =>
                  onInputChange(
                    'other_allowances',
                    e.target.value === '' ? '' : String(Math.max(0, parseFloat(e.target.value) || 0))
                  )
                }
                disabled={isViewMode}
                helperText="Bonuses, housing, or other recurring allowances."
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.salary_currency}</InputAdornment>,
                  inputProps: { min: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Deductions"
                type="number"
                value={formData.tax_deductions}
                onChange={(e) =>
                  onInputChange(
                    'tax_deductions',
                    e.target.value === '' ? '' : String(Math.max(0, parseFloat(e.target.value) || 0))
                  )
                }
                disabled={isViewMode}
                helperText="Withheld tax or statutory deductions."
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.salary_currency}</InputAdornment>,
                  inputProps: { min: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Take Home Salary (Auto-calculated)"
                type="number"
                value={formData.take_home_salary}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">{formData.salary_currency}</InputAdornment>,
                  readOnly: true,
                }}
                helperText="Automatically calculated from (Basic Salary + Transport Allowance + Other Allowances) - Tax Deductions"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <FormSectionHeader
            title="Employment dates"
            description="Joining date is used for tenure. A resignation date marks the employee inactive automatically."
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Joining Date"
                      value={formData.joining_date ? dayjs(formData.joining_date) : null}
                      onChange={(value) => {
                        const formattedDate = value ? dayjs(value).format('YYYY-MM-DD') : '';
                        onInputChange('joining_date', formattedDate);
                      }}
                      disabled={isViewMode}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: 'First day of employment.',
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Resignation Date"
                      value={formData.resignation_date ? dayjs(formData.resignation_date) : null}
                      onChange={(value) => {
                        const formattedDate = value ? dayjs(value).format('YYYY-MM-DD') : null;
                        onInputChange('resignation_date', formattedDate);
                      }}
                      disabled={isViewMode}
                      sx={{ width: '100%' }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: 'Leave empty if employee is still active',
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active || false}
                    onChange={(e) => onInputChange('is_active', e.target.checked)}
                    disabled={isViewMode || hasResignationDate(formData.resignation_date)}
                  />
                }
                label="Active employee"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: hasResignationDate(formData.resignation_date) ? 'text.secondary' : 'text.primary',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {hasResignationDate(formData.resignation_date)
                  ? 'Inactive while a resignation date is set.'
                  : 'Inactive employees are excluded from active-only lists and new assignments.'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <FormSectionHeader
            title="Bank information"
            description="Optional — used for payroll disbursement. Leave blank if not paying via bank transfer."
          />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bank_name}
                onChange={(e) => onInputChange('bank_name', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.bank_name}
                helperText={formErrors.bank_name || 'Name of the financial institution.'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bank_account_number}
                onChange={(e) => onInputChange('bank_account_number', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.bank_account_number}
                helperText={formErrors.bank_account_number || 'Account number for salary deposits.'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Title"
                value={formData.bank_account_title}
                onChange={(e) => onInputChange('bank_account_title', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.bank_account_title}
                helperText={formErrors.bank_account_title || 'Name on the account as registered with the bank.'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <FormSectionHeader
            title="Additional information"
            description="Internal notes visible to HR only — not shown on employee-facing documents."
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={formData.remarks}
                onChange={(e) => onInputChange('remarks', e.target.value)}
                disabled={isViewMode}
                helperText="Optional context for HR (e.g. contract type, special arrangements)."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode !== 'view' && (
          <Button onClick={onSubmit} variant="contained">
            {mode === 'add' ? 'Add Employee' : 'Update Employee'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeForm;
